// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState } from 'react'
import { HardDrive, RefreshCw, Loader2, AlertCircle, Pencil, Check, X } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

const GB = 1073741824

function fmtBytes(n: number): string {
  if (n >= GB) return `${(n / GB).toFixed(2)} GB`
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${n} B`
}
function ageLabel(iso: string | null): string {
  if (!iso) return 'never'
  const mins = Math.max(0, (Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 60) return `${Math.round(mins)} min ago`
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`
  return `${Math.round(mins / 1440)} d ago`
}
function barColor(pct: number): string {
  if (pct >= 100) return '#dc2626'
  if (pct >= 80) return '#d97706'
  return '#0d9488'
}

// D-UI-2: what each column shows, for its header funnel.
const STORAGE_FUNNELS: FunnelColumn<api.OpsStoragePharmacy>[] = [
  { key: 'pharmacy', label: 'Pharmacy',               value: p => p.name || p.slug },
  { key: 'patient',  label: 'Patient attachments',    value: p => fmtBytes(p.patient_bytes) },
  { key: 'limit',    label: 'Limit',                  value: p => fmtBytes(p.quota_bytes) },
  { key: 'used',     label: 'Utilization',            value: p => `${p.used_pct}%` },
  { key: 'exempt',   label: 'Exempt (CareRx + docs)', value: p => `${fmtBytes(p.exempt_bytes)}${p.rollup_stale ? ' *' : ''}` },
  { key: 'enforced', label: 'Enforced',               value: p => p.enforced ? 'ENFORCED' : 'off' },
]

/**
 * System Monitor → Storage. SaaS-wide per-pharmacy storage. The LIMIT applies to PATIENT ATTACHMENTS
 * (patient_documents) only. CareRx module documents and pharmacy compliance docs are shown for
 * visibility but are EXEMPT. Platform admins set each pharmacy's limit and toggle enforcement inline.
 */
export default function StorageTab() {
  const [data, setData] = useState<api.OpsStorageResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [recomputing, setRecomputing] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [draftGb, setDraftGb] = useState('')
  const [draftEnforced, setDraftEnforced] = useState(false)
  const [saving, setSaving] = useState(false)

  async function load() {
    setLoading(true); setErr(null)
    try { setData(await api.getOpsStorage()) }
    catch (e) { setErr(friendlyError(e)) }
    finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  async function recompute() {
    setRecomputing(true); setErr(null)
    try { await api.recomputeOpsStorage(); await load() }
    catch (e) { setErr(friendlyError(e)) }
    finally { setRecomputing(false) }
  }

  function startEdit(p: api.OpsStoragePharmacy) {
    setEditId(p.pharmacy_id)
    setDraftGb((p.quota_bytes / GB).toString())
    setDraftEnforced(p.enforced)
  }
  async function saveEdit(id: string) {
    setSaving(true); setErr(null)
    try {
      const gb = Math.max(0, parseFloat(draftGb) || 0)
      await api.setOpsStoragePolicy(id, { quota_bytes: Math.round(gb * GB), enforced: draftEnforced })
      setEditId(null); await load()
    } catch (e) { setErr(friendlyError(e)) }
    finally { setSaving(false) }
  }

  // The header funnels filter the whole loaded list (sorted by usage first).
  const pharmacies = useMemo(
    () => data ? [...data.pharmacies].sort((a, b) => b.patient_bytes - a.patient_bytes) : [], [data])
  const fx = useTableFunnels(pharmacies, STORAGE_FUNNELS)

  if (loading && !data) return <div style={{ padding: 24, color: 'var(--color-text-soft)' }}><Loader2 size={16} className="spin" /> Loading…</div>

  const totalPct = data && data.total_quota_bytes > 0
    ? Math.min(100, Math.round(100 * data.total_patient_bytes / data.total_quota_bytes * 10) / 10) : 0
  const totalExempt = data ? data.total_carerx_bytes + data.total_pharmacy_doc_bytes : 0

  return (
    <div style={{ maxWidth: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
          <HardDrive size={18} /> Storage
        </h2>
        <button onClick={recompute} disabled={recomputing} style={btn}>
          {recomputing ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />}
          {recomputing ? 'Recomputing…' : 'Recompute now'}
        </button>
      </div>

      {err && <div style={errBox}><AlertCircle size={14} /> {err}</div>}

      {data && (
        <>
          <div style={card}>
            <div style={{ fontSize: 13, color: 'var(--color-text-soft)', marginBottom: 6 }}>Patient-attachment usage (the enforced limit)</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
              <strong style={{ fontSize: 22 }}>{fmtBytes(data.total_patient_bytes)}</strong>
              <span style={{ color: 'var(--color-text-soft)', fontSize: 14 }}>
                of {fmtBytes(data.total_quota_bytes)} allocated across {data.pharmacies.length} pharmacies ({totalPct}%)
              </span>
            </div>
            <div style={{ height: 10, borderRadius: 6, background: '#f0efed', overflow: 'hidden' }}>
              <div style={{ width: `${totalPct}%`, height: '100%', background: '#0d9488' }} />
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-mute)' }}>
              Exempt: CareRx {fmtBytes(data.total_carerx_bytes)} &middot; compliance docs {fmtBytes(data.total_pharmacy_doc_bytes)} ({fmtBytes(totalExempt)} not counted)
              {' \u00b7 '}rollup as of {ageLabel(data.oldest_computed_at)}
              {data.any_stale && <span style={{ color: '#d97706' }}> &middot; some pharmacies not yet computed — run “Recompute now”</span>}
            </div>
          </div>

          {fx.chips}
          <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-text-soft)', borderBottom: '1px solid var(--color-border)' }}>
                <th style={th}>Pharmacy {fx.funnel('pharmacy')}</th>
                <th style={th}>Patient attachments {fx.funnel('patient')}</th>
                <th style={th}>Limit {fx.funnel('limit')}</th>
                <th style={{ ...th, width: 170 }}>Utilization {fx.funnel('used')}</th>
                <th style={th}>Exempt (CareRx + docs) {fx.funnel('exempt')}</th>
                <th style={th}>Enforced {fx.funnel('enforced')}</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {fx.rows.map(p => {
                const c = barColor(p.used_pct)
                const editing = editId === p.pharmacy_id
                return (
                  <tr key={p.pharmacy_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={td}><strong>{p.name || p.slug}</strong></td>
                    <td style={td}>{fmtBytes(p.patient_bytes)}</td>
                    <td style={td}>
                      {editing
                        ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <input type="number" min={0} step={1} value={draftGb} onChange={e => setDraftGb(e.target.value)}
                                   style={{ width: 64, padding: '3px 6px', fontSize: 13, border: '1px solid var(--color-border)', borderRadius: 6 }} /> GB
                          </span>
                        : fmtBytes(p.quota_bytes)}
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 8, borderRadius: 5, background: '#f0efed', overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(100, p.used_pct)}%`, height: '100%', background: c }} />
                        </div>
                        <span style={{ color: c, fontVariantNumeric: 'tabular-nums', minWidth: 42, textAlign: 'right' }}>{p.used_pct}%</span>
                      </div>
                    </td>
                    <td style={{ ...td, color: 'var(--color-text-mute)' }}>{fmtBytes(p.exempt_bytes)}{p.rollup_stale && ' *'}</td>
                    <td style={td}>
                      {editing
                        ? <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            <input type="checkbox" checked={draftEnforced} onChange={e => setDraftEnforced(e.target.checked)} /> on
                          </label>
                        : <span style={{ fontSize: 12, fontWeight: 600, color: p.enforced ? '#dc2626' : 'var(--color-text-mute)' }}>
                            {p.enforced ? 'ENFORCED' : 'off'}
                          </span>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {editing
                        ? <span style={{ display: 'inline-flex', gap: 4 }}>
                            <button onClick={() => saveEdit(p.pharmacy_id)} disabled={saving} style={iconBtn} title="Save">
                              {saving ? <Loader2 size={14} className="spin" /> : <Check size={14} />}
                            </button>
                            <button onClick={() => setEditId(null)} disabled={saving} style={iconBtn} title="Cancel"><X size={14} /></button>
                          </span>
                        : <button onClick={() => startEdit(p)} style={iconBtn} title="Set limit / enforcement"><Pencil size={13} /></button>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--color-text-mute)' }}>
            The limit applies to patient attachments only. CareRx module documents and pharmacy compliance docs do not count and are never blocked.
            {data.any_stale && ' * figure not yet computed for this pharmacy.'}
          </div>
        </>
      )}
    </div>
  )
}

const card: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 10, padding: 16, marginBottom: 20, background: 'var(--color-surface)' }
const th: React.CSSProperties = { padding: '8px 10px', fontWeight: 600 }
const td: React.CSSProperties = { padding: '10px' }
const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', fontSize: 13, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer' }
const iconBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 6, borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer' }
const errBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, background: '#fef2f2', color: '#dc2626', fontSize: 13, marginBottom: 14 }
